import React from 'react';
import BankAccountService, { BankAccount, CreateBankAccountPayload, UpdateBankAccountPayload } from '../services/BankAccountService';

interface BankAccountFormProps {
  account?: BankAccount | null;
  prefillExample?: boolean;
  onSuccess: (account?: BankAccount) => void;
  onCancel: () => void;
}

const TEST_IBANS_DE = [
  'DE89370400440532013000',
  'DE12500105170648489890',
  'DE75512108001245126199',
  'DE44500105175407324931',
];

const IBAN_LENGTHS: Record<string, number> = {
  AD: 24, AE: 23, AL: 28, AT: 20, AZ: 28, BA: 20, BE: 16,
  BG: 22, BH: 22, BR: 29, BY: 28, CH: 21, CR: 22, CY: 28,
  CZ: 24, DE: 22, DK: 18, DO: 28, EE: 20, EG: 29, ES: 24,
  FI: 18, FO: 18, FR: 27, GB: 22, GE: 22, GI: 23, GL: 18,
  GR: 27, GT: 28, HR: 21, HU: 28, IE: 22, IL: 23, IS: 26,
  IT: 27, JO: 30, KW: 30, KZ: 20, LB: 28, LC: 32, LI: 21,
  LT: 20, LU: 20, LV: 21, MC: 27, MD: 24, ME: 22, MK: 19,
  MR: 27, MT: 31, MU: 30, NL: 18, NO: 15, PK: 24, PL: 28,
  PS: 29, PT: 25, QA: 29, RO: 24, RS: 22, SA: 24, SE: 24,
  SI: 19, SK: 24, SM: 27, TN: 24, TR: 26, UA: 29, VA: 22,
  VG: 24, XK: 20,
};

function validateIBANClientSide(iban: string): { valid: boolean; error?: string } {
  const cleaned = iban.replace(/\s|-/g, '').toUpperCase();

  if (!cleaned) {
    return { valid: false, error: 'IBAN ist erforderlich' };
  }

  if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]+$/.test(cleaned)) {
    return { valid: false, error: 'IBAN Format ungültig (muss mit 2 Buchstaben + 2 Ziffern beginnen)' };
  }

  const countryCode = cleaned.slice(0, 2);
  const expectedLength = IBAN_LENGTHS[countryCode];

  if (!expectedLength) {
    return { valid: false, error: `Unbekanntes Länderkürzel (${countryCode})` };
  }

  if (cleaned.length !== expectedLength) {
    return {
      valid: false,
      error: `${countryCode}-IBAN muss ${expectedLength} Zeichen haben (aktuell: ${cleaned.length})`
    };
  }

  // MOD-97 Check
  const rearranged = cleaned.slice(4) + cleaned.slice(0, 4);
  const numeric = rearranged.replace(/[A-Z]/g, (char) => String(char.charCodeAt(0) - 55));

  let checksum = 0;
  for (let i = 0; i < numeric.length; i++) {
    checksum = (checksum * 10 + parseInt(numeric[i], 10)) % 97;
  }

  if (checksum !== 1) {
    return { valid: false, error: 'IBAN Prüfsumme ungültig (MOD-97)' };
  }

  return { valid: true };
}

const BankAccountForm: React.FC<BankAccountFormProps> = ({ account, prefillExample, onSuccess, onCancel }) => {
  const isEditMode = !!account;

  const [formData, setFormData] = React.useState({
    name: account?.name || '',
    holder_name: account?.holder_name || '',
    iban: '',              // nur für Create-Modus
    newIban: '',           // nur für Edit-Modus (neue IBAN optional)
    bic: account?.bic || '',
    bank_name: account?.bank_name || '',
    is_payroll_default: account?.is_payroll_default || false,
  });

  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState('');

  const fillExample = () => {
    setFormData({
      name: 'Haupt-Lohnkonto',
      holder_name: 'SundaeOS GmbH',
      iban: 'DE89 3704 0044 0532 0130 00',
      newIban: '',
      bic: 'COBADEFFXXX',
      bank_name: 'Commerzbank',
      is_payroll_default: true,
    });
    setErrors({});
  };

  React.useEffect(() => {
    if (prefillExample && !account) {
      fillExample();
    }
  }, [prefillExample, account]);

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateIBAN = async (iban: string) => {
    const cleaned = iban.replace(/\s|-/g, '').toUpperCase();
    const clientValidation = validateIBANClientSide(cleaned);
    if (!clientValidation.valid) {
      return clientValidation.error || 'IBAN ungültig';
    }
    return null;
  };

  const handleIBANBlur = async () => {
    if (formData.iban && !isEditMode) {
      const error = await validateIBAN(formData.iban);
      if (error) {
        setErrors((prev) => ({ ...prev, iban: error }));
      }
    }
  };

  const handleNewIBANBlur = async () => {
    if (formData.newIban.trim()) {
      const error = await validateIBAN(formData.newIban);
      if (error) {
        setErrors((prev) => ({ ...prev, newIban: error }));
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setErrors({});

    const newErrors: Record<string, string> = {};

    // Common validations
    if (!formData.name.trim()) newErrors.name = 'Name ist erforderlich';
    if (!formData.holder_name.trim()) newErrors.holder_name = 'Kontoinhaber ist erforderlich';

    if (isEditMode) {
      // Edit-Modus: Neue IBAN ist optional
      if (formData.newIban.trim()) {
        const ibanError = await validateIBAN(formData.newIban);
        if (ibanError) {
          newErrors.newIban = ibanError;
        }
      }
    } else {
      // Create-Modus: IBAN ist erforderlich
      if (!formData.iban.trim()) {
        newErrors.iban = 'IBAN ist erforderlich';
      } else {
        const ibanError = await validateIBAN(formData.iban);
        if (ibanError) {
          newErrors.iban = ibanError;
        }
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSubmitting(true);
    try {
      let resultAccount: BankAccount;

      if (isEditMode) {
        // Edit-Modus: IBAN nur senden wenn newIban befüllt
        const payload: UpdateBankAccountPayload = {
          name: formData.name.trim(),
          holder_name: formData.holder_name.trim(),
          bic: formData.bic.trim() || null,
          bank_name: formData.bank_name.trim() || null,
          is_payroll_default: formData.is_payroll_default,
        };

        // Nur IBAN hinzufügen wenn neue IBAN eingegeben wurde
        if (formData.newIban.trim()) {
          payload.iban = formData.newIban.trim();
        }

        resultAccount = await BankAccountService.updateBankAccount(account!.id, payload);
      } else {
        // Create-Modus
        const payload: CreateBankAccountPayload = {
          name: formData.name.trim(),
          holder_name: formData.holder_name.trim(),
          iban: formData.iban.trim(),
          bic: formData.bic.trim() || undefined,
          bank_name: formData.bank_name.trim() || undefined,
          currency: 'EUR',
          is_payroll_default: formData.is_payroll_default,
        };

        resultAccount = await BankAccountService.createBankAccount(payload);
      }

      onSuccess(resultAccount);
    } catch (e: any) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message;

      if (status === 401) {
        setError('Sitzung abgelaufen – bitte neu anmelden.');
      } else if (status === 400) {
        const responseErrors = e?.response?.data;
        if (typeof responseErrors === 'object') {
          const fieldErrors: Record<string, string> = {};
          Object.keys(responseErrors).forEach((key) => {
            const value = responseErrors[key];
            if (Array.isArray(value)) {
              fieldErrors[key] = value[0];
            } else if (typeof value === 'string') {
              fieldErrors[key] = value;
            }
          });
          setErrors(fieldErrors);
        } else {
          setError(detail || 'Validierungsfehler');
        }
      } else {
        setError(`Fehler: ${detail || 'Unbekannter Fehler'}`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {error && (
        <div
          style={{
            padding: 12,
            marginBottom: 16,
            background: '#ffebee',
            border: '1px solid #ef5350',
            borderRadius: 6,
            color: '#c62828',
            fontSize: 14,
          }}
        >
          {error}
        </div>
      )}

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: '#0f2b5a' }}>
          Name / Bezeichnung *
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => handleChange('name', e.target.value)}
          placeholder="z.B. Haupt-Lohnkonto"
          style={{
            width: '100%',
            padding: '10px 12px',
            border: `1px solid ${errors.name ? '#ef5350' : '#d0d4e4'}`,
            borderRadius: 6,
            fontSize: 14,
            outline: 'none',
          }}
        />
        {errors.name && <div style={{ marginTop: 4, fontSize: 13, color: '#ef5350' }}>{errors.name}</div>}
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: '#0f2b5a' }}>
          Kontoinhaber *
        </label>
        <input
          type="text"
          value={formData.holder_name}
          onChange={(e) => handleChange('holder_name', e.target.value)}
          placeholder="z.B. SundaeOS GmbH"
          style={{
            width: '100%',
            padding: '10px 12px',
            border: `1px solid ${errors.holder_name ? '#ef5350' : '#d0d4e4'}`,
            borderRadius: 6,
            fontSize: 14,
            outline: 'none',
          }}
        />
        {errors.holder_name && (
          <div style={{ marginTop: 4, fontSize: 13, color: '#ef5350' }}>{errors.holder_name}</div>
        )}
      </div>

      {/* IBAN-Felder: Create vs. Edit */}
      {isEditMode ? (
        <>
          {/* Edit-Modus: Maskierte IBAN (read-only) + Neue IBAN (optional) */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: '#0f2b5a' }}>
              IBAN (aktuell)
            </label>
            <input
              type="text"
              value={account?.iban_masked || '••••'}
              disabled
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #d0d4e4',
                borderRadius: 6,
                fontSize: 14,
                fontFamily: 'monospace',
                outline: 'none',
                background: '#f9fafb',
                color: '#666',
              }}
            />
            <div style={{ marginTop: 4, fontSize: 12, color: '#666' }}>
              Aktuelle IBAN (maskiert aus Sicherheitsgründen)
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: '#0f2b5a' }}>
              Neue IBAN (optional)
            </label>
            <input
              type="text"
              value={formData.newIban}
              onChange={(e) => handleChange('newIban', e.target.value)}
              onBlur={handleNewIBANBlur}
              placeholder="Nur eingeben wenn IBAN geändert werden soll"
              style={{
                width: '100%',
                padding: '10px 12px',
                border: `1px solid ${errors.newIban ? '#ef5350' : '#d0d4e4'}`,
                borderRadius: 6,
                fontSize: 14,
                fontFamily: 'monospace',
                outline: 'none',
              }}
            />
            {errors.newIban && <div style={{ marginTop: 4, fontSize: 13, color: '#ef5350' }}>{errors.newIban}</div>}
            <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
              <select
                onChange={(e) => handleChange('newIban', e.target.value)}
                value=""
                style={{
                  padding: '6px 10px',
                  border: '1px solid #d0d4e4',
                  borderRadius: 6,
                  fontSize: 13,
                  color: '#666',
                  cursor: 'pointer',
                  outline: 'none',
                }}
              >
                <option value="" disabled>Test-IBAN auswählen…</option>
                {TEST_IBANS_DE.map((v) => (
                  <option key={v} value={v}>{v.replace(/(.{4})/g, '$1 ').trim()}</option>
                ))}
              </select>
              <span style={{ fontSize: 12, color: '#666' }}>
                Leer lassen, um IBAN unverändert zu lassen
              </span>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Create-Modus: Normale IBAN (erforderlich) */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: '#0f2b5a' }}>
              IBAN *
            </label>
            <input
              type="text"
              value={formData.iban}
              onChange={(e) => handleChange('iban', e.target.value)}
              onBlur={handleIBANBlur}
              placeholder="DE89 3704 0044 0532 0130 00"
              style={{
                width: '100%',
                padding: '10px 12px',
                border: `1px solid ${errors.iban ? '#ef5350' : '#d0d4e4'}`,
                borderRadius: 6,
                fontSize: 14,
                fontFamily: 'monospace',
                outline: 'none',
              }}
            />
            {errors.iban && <div style={{ marginTop: 4, fontSize: 13, color: '#ef5350' }}>{errors.iban}</div>}
            <div style={{ marginTop: 8 }}>
              <select
                onChange={(e) => handleChange('iban', e.target.value)}
                value=""
                style={{
                  padding: '6px 10px',
                  border: '1px solid #d0d4e4',
                  borderRadius: 6,
                  fontSize: 13,
                  color: '#666',
                  cursor: 'pointer',
                  outline: 'none',
                }}
              >
                <option value="" disabled>Test-IBAN auswählen…</option>
                {TEST_IBANS_DE.map((v) => (
                  <option key={v} value={v}>{v.replace(/(.{4})/g, '$1 ').trim()}</option>
                ))}
              </select>
            </div>
          </div>
        </>
      )}

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: '#0f2b5a' }}>
          BIC (optional)
        </label>
        <input
          type="text"
          value={formData.bic}
          onChange={(e) => handleChange('bic', e.target.value)}
          placeholder="z.B. COBADEFFXXX"
          style={{
            width: '100%',
            padding: '10px 12px',
            border: `1px solid ${errors.bic ? '#ef5350' : '#d0d4e4'}`,
            borderRadius: 6,
            fontSize: 14,
            fontFamily: 'monospace',
            outline: 'none',
          }}
        />
        {errors.bic && <div style={{ marginTop: 4, fontSize: 13, color: '#ef5350' }}>{errors.bic}</div>}
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 600, color: '#0f2b5a' }}>
          Bankname (optional)
        </label>
        <input
          type="text"
          value={formData.bank_name}
          onChange={(e) => handleChange('bank_name', e.target.value)}
          placeholder="z.B. Commerzbank"
          style={{
            width: '100%',
            padding: '10px 12px',
            border: `1px solid ${errors.bank_name ? '#ef5350' : '#d0d4e4'}`,
            borderRadius: 6,
            fontSize: 14,
            outline: 'none',
          }}
        />
        {errors.bank_name && <div style={{ marginTop: 4, fontSize: 13, color: '#ef5350' }}>{errors.bank_name}</div>}
      </div>

      <div style={{ marginBottom: 24 }}>
        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: 14 }}>
          <input
            type="checkbox"
            checked={formData.is_payroll_default}
            onChange={(e) => handleChange('is_payroll_default', e.target.checked)}
            style={{ marginRight: 8 }}
          />
          Als Standard-Lohnkonto verwenden
        </label>
        <div style={{ marginTop: 4, fontSize: 12, color: '#666' }}>
          Das Standard-Lohnkonto wird automatisch für neue Lohnläufe verwendet.
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between' }}>
        {!isEditMode && (
          <button
            type="button"
            onClick={fillExample}
            disabled={submitting}
            style={{
              padding: '10px 20px',
              background: '#fff',
              color: '#2196f3',
              border: '1px solid #2196f3',
              borderRadius: 6,
              cursor: submitting ? 'not-allowed' : 'pointer',
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            Fill with Example
          </button>
        )}
        <div style={{ display: 'flex', gap: 12, marginLeft: 'auto' }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            style={{
              padding: '10px 20px',
              background: '#fff',
              color: '#666',
              border: '1px solid #d0d4e4',
              borderRadius: 6,
              cursor: submitting ? 'not-allowed' : 'pointer',
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={submitting}
            style={{
              padding: '10px 20px',
              background: submitting ? '#ccc' : '#021a4a',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              cursor: submitting ? 'not-allowed' : 'pointer',
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            {submitting ? 'Speichert...' : isEditMode ? 'Aktualisieren' : 'Hinzufügen'}
          </button>
        </div>
      </div>
    </form>
  );
};

export default BankAccountForm;
